/**
 * Role-based access control utilities
 */

export interface CompanyUser {
  email: string;
  role: string;
  uid?: string;
  id?: string;
}

export interface CurrentUser {
  email: string;
  uid?: string;
}

// Role constants - these should match backend constants
export const ROLES = {
  OWNER: 'owner',
  ADMIN: 'admin', 
  MEMBER: 'member'
} as const;

/**
 * Check if user has owner role
 */
export const isOwner = (currentUser: CurrentUser, companyUsers: CompanyUser[]): boolean => {
  const companyUser = companyUsers.find((x) => x.email === currentUser.email);
  return companyUser ? companyUser.role === ROLES.OWNER : false;
};

/**
 * Check if user has admin role
 */
export const isAdmin = (currentUser: CurrentUser, companyUsers: CompanyUser[]): boolean => {
  const companyUser = companyUsers.find((x) => x.email === currentUser.email);
  return companyUser ? companyUser.role === ROLES.ADMIN : false;
};

/**
 * Check if user has member role
 */
export const isMember = (currentUser: CurrentUser, companyUsers: CompanyUser[]): boolean => {
  const companyUser = companyUsers.find((x) => x.email === currentUser.email);
  return companyUser ? companyUser.role === ROLES.MEMBER : false;
};

/**
 * Check if user has admin or owner role (elevated permissions)
 */
export const hasAdminAccess = (currentUser: CurrentUser, companyUsers: CompanyUser[]): boolean => {
  return isOwner(currentUser, companyUsers) || isAdmin(currentUser, companyUsers);
};

/**
 * Check if user has owner or admin role - for features requiring elevated permissions
 */
export const canAccessAdminFeatures = (currentUser: CurrentUser, companyUsers: CompanyUser[]): boolean => {
  return hasAdminAccess(currentUser, companyUsers);
};

/**
 * Check if user can access dashboard analytics
 * Restricted to admin and owner roles only
 */
export const canAccessDashboard = (currentUser: CurrentUser, companyUsers: CompanyUser[]): boolean => {
  return canAccessAdminFeatures(currentUser, companyUsers);
};

/**
 * Check if user can access integrations
 * Restricted to admin and owner roles only  
 */
export const canAccessIntegrations = (currentUser: CurrentUser, companyUsers: CompanyUser[]): boolean => {
  return canAccessAdminFeatures(currentUser, companyUsers);
};

/**
 * Check if user can access collaborators management
 * Restricted to admin and owner roles only
 */
export const canAccessCollaborators = (currentUser: CurrentUser, companyUsers: CompanyUser[]): boolean => {
  return canAccessAdminFeatures(currentUser, companyUsers);
};

// New functions that work with a single user role (more efficient)

/**
 * Check if user has owner role - single user version
 */
export const isOwnerByRole = (userRole: string | null): boolean => {
  return userRole === ROLES.OWNER;
};

/**
 * Check if user has admin role - single user version
 */
export const isAdminByRole = (userRole: string | null): boolean => {
  return userRole === ROLES.ADMIN;
};

/**
 * Check if user has admin or owner role - single user version
 */
export const hasAdminAccessByRole = (userRole: string | null): boolean => {
  return userRole === ROLES.OWNER || userRole === ROLES.ADMIN;
};

/**
 * Check if user can access dashboard analytics - single user version
 */
export const canAccessDashboardByRole = (userRole: string | null): boolean => {
  return hasAdminAccessByRole(userRole);
};

/**
 * Check if user can access integrations - single user version
 */
export const canAccessIntegrationsByRole = (userRole: string | null): boolean => {
  return hasAdminAccessByRole(userRole);
};

/**
 * Check if user can access collaborators - single user version
 */
export const canAccessCollaboratorsByRole = (userRole: string | null): boolean => {
  return hasAdminAccessByRole(userRole);
};

/**
 * Get user's current role in the company
 */
export const getUserRole = (currentUser: CurrentUser, companyUsers: CompanyUser[]): string | null => {
  const companyUser = companyUsers.find((x) => x.email === currentUser.email);
  return companyUser ? companyUser.role : null;
};

/**
 * Check if user has specific role
 */
export const hasRole = (currentUser: CurrentUser, companyUsers: CompanyUser[], role: string): boolean => {
  const userRole = getUserRole(currentUser, companyUsers);
  return userRole === role;
};

/**
 * Check if user has any of the specified roles
 */
export const hasAnyRole = (currentUser: CurrentUser, companyUsers: CompanyUser[], roles: string[]): boolean => {
  const userRole = getUserRole(currentUser, companyUsers);
  return userRole ? roles.includes(userRole) : false;
};